import React from 'react'
import styled, { css } from 'styled-components'
import { Text, defaultTheme, Link as StormLink, isMobileStyleMixin } from '@amzn/storm-ui'
import { PageBanner as StormUIPageBanner, MediaContainer, TextContainer } from '@amzn/storm-ui-page-banner'
import { useMetadataContext } from '../../contexts/MetadataContext'
import { useEmitInitialRender } from '../../utils/hooks'
import { ActionProp, ActionType, ImageProp } from '../../common/props'
import { dismissWidget } from '../../utils/functions'
import { WidgetTypes } from '../../common/constants'
import { logWithTakt } from '../../utils/takt'

export interface Paragraph {
  subtitle: string
  content: string
}

export interface ExpandedPageBannerProps {
  type: string
  anchorId: string
  title: string
  paragraphs: Paragraph[]
  action?: ActionProp
  image?: ImageProp
  imageSize?: 'small' | 'medium' | 'base'
  hideLabel: string
  revealLabel: string
  closeAriaLabel: string
  dataTaktId: string
}

const PageBanner = styled(StormUIPageBanner)`
  img {
    max-width: unset;
    box-sizing: initial;
    background-color: transparent;
  }
`

const TextContainerWithSpacing = styled.div`
  margin-bottom: ${defaultTheme.spacing.medium};
`

const TitleText = styled(Text)`
  line-height: 31px;
  ${isMobileStyleMixin(
    css`
      font-size: 18px;
      line-height: 26px;
    `
  )}
`

const SubtitleText = styled(Text)`
  line-height: 25px;
  margin-bottom: ${defaultTheme.spacing.micro};
  ${isMobileStyleMixin(
    css`
      font-size: 15px;
      line-height: 22px;
      margin-bottom: ${defaultTheme.spacing.mini};
    `
  )}
`

const contentStyle = css`
  font-size: 15px;
  line-height: 21px;
  ${isMobileStyleMixin(
    css`
      line-height: 22px;
    `
  )}
`

const ContentText = styled(Text)`
  ${contentStyle}
`

const Link = styled(StormLink)`
  ${contentStyle}
`

const TextContent: React.FC<{ title: string; content: Paragraph[] }> = ({ title, content }) => (
  <>
    <TextContainerWithSpacing>
      <TitleText type="h2">{title}</TitleText>
    </TextContainerWithSpacing>
    {content.length === 1 ? (
      <TextContainerWithSpacing>
        <ContentText type="p">{content[0].content}</ContentText>
      </TextContainerWithSpacing>
    ) : (
      content.map((content) => {
        return (
          <TextContainerWithSpacing key={content.subtitle}>
            <SubtitleText type="h3">{content.subtitle}</SubtitleText>
            <ContentText type="p">{content.content}</ContentText>
          </TextContainerWithSpacing>
        )
      })
    )}
  </>
)

export const ExpandedPageBanner: React.FC<ExpandedPageBannerProps> = (props) => {
  const [shouldShow, setShouldShow] = React.useState(true)
  const metadataContext = useMetadataContext()
  const content = <TextContent title={props.title} content={props.paragraphs} />

  useEmitInitialRender(WidgetTypes.ExpandedPageBanner)

  const onBannerClose = () => {
    dismissWidget(props.anchorId)
    setShouldShow(false)
    logWithTakt(`${WidgetTypes.ExpandedPageBanner}${metadataContext.isMobile ? 'Mobile' : 'Desktop'}`, { event: 'close' })
  }

  if (!shouldShow) {
    return null
  }

  return (
    <PageBanner data-takt-id={props.dataTaktId} closeButtonAriaLabel={props.closeAriaLabel} onClose={onBannerClose}>
      {props.image && <MediaContainer alt={props.image.altText} size={props.imageSize} src={props.image.link} />}
      <TextContainer hideLabel={props.hideLabel} revealLabel={props.revealLabel} renderTeaser={metadataContext.isMobile ? content : undefined}>
        {metadataContext.isMobile ? null : content}
        {props.action && props.action.link && (
          <Link
            href={props.action.link.link}
            external={props.action.type === ActionType.OPEN_EXTERNAL_ACTION}
            data-takt-id={`${props.dataTaktId}_link`}
            {...(props.action.type === ActionType.OPEN_EXTERNAL_ACTION ? { target: '_blank' } : {})}
          >
            {props.action.link.label}
          </Link>
        )}
      </TextContainer>
    </PageBanner>
  )
}

ExpandedPageBanner.defaultProps = {
  imageSize: 'base',
  hideLabel: 'Dismiss',
  revealLabel: 'Show more',
}
